<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'
import EditStudentGroups from '@/components/SidePanels/EditStudent/EditStudentGroups.vue'
import LicensesList from '@/components/SidePanels/EditStudent/LicensesList.vue'
import Dismissable from '@/components/Dismissable.vue'
import { licenseModule } from '@/store/license/module'
import { studentModule } from '@/store/student/module'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { bundleModule } from '@/store/bundle/module'
import { toastModule } from '@/store/toast/module'
import { computed, onMounted, ref } from 'vue'

const {
    SidePanel,
    Button: PocketButton,
    Input: PocketInput,
    Textarea: PocketTextarea,
    ToggleSwitch,
} = UIKit

const props = defineProps<{
    orgStudentId: string
}>()

const emit = defineEmits<{
    'close': []
}>()

const isLoading = ref(false)
const customMessage = ref('')
const email = ref('')
const enableDisplayName = ref(false)
const displayFirstName = ref('')
const displayLastName = ref('')
const updatedInstructorOptionsPerExamGuid = ref<{ [key: string]: string[] }>({})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const currentOrgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const currentOrgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const licenses = computed(() => {
    const processedLicenses = currentOrgLicenses.value
        .filter(l => l.orgStudent && l.orgStudent.objectId === props.orgStudentId)
        .sort((a, b) => a.status === 'consumed' ? -1 : a.status === 'pending' && b.status === 'archived' ? -1 : 1)
    return processedLicenses
})

const orgStudent = computed(() => {
    return currentOrgStudents.value?.[props.orgStudentId]
})

const orgStudentUser = computed(() => {
    return orgStudent.value?.user && currentOrgStudentUsers.value[orgStudent.value.user.objectId]
})

const studentEmail = computed(() => {
    const pendingLicense = licenses.value.find(l => l.status === 'pending')
    const userEmail = orgStudentUser.value?.username
    return userEmail || pendingLicense?.userEmail || '--'
})

const studentName = computed(() => {
    return orgStudent.value?.displayFirstName || orgStudent.value?.displayLastName
        ? [ orgStudent.value.displayFirstName, orgStudent.value.displayLastName ].join(' ')
        : orgStudentUser.value
            ? [ orgStudentUser.value.firstName, orgStudentUser.value.lastName ].join(' ')
            : studentEmail.value
})

const licenseMetrics = computed(() => {
    const lMetrics = licenses.value.reduce((acc, l) => {
        if (l.status === 'pending') {
            acc.pending++
        }
        if (l.status === 'consumed') {
            acc.active++
        }
        if (l.status === 'archived') {
            acc.archived++
        }

        return acc
    }, { pending: 0, active: 0, archived: 0 })
    return lMetrics
})

const studentLicenseSummary = computed(() => {
    if (licenseMetrics.value.pending && !licenseMetrics.value.active && !licenseMetrics.value.archived) {
        return `${studentName.value} is invited to study for ${licenseMetrics.value.pending} exam${
            licenseMetrics.value.pending > 1 ? 's' : ''}`
    } else if (!licenseMetrics.value.pending && licenseMetrics.value.active && !licenseMetrics.value.archived) {
        return `${studentName.value} is studying for ${licenseMetrics.value.active} exam${
            licenseMetrics.value.active > 1 ? 's' : ''}`
    } else if (!licenseMetrics.value.pending && !licenseMetrics.value.active && licenseMetrics.value.archived) {
        return `${studentName.value} studied for ${licenseMetrics.value.archived} exam${
            licenseMetrics.value.archived > 1 ? 's' : ''}`
    } else {
        const statusArr = [ `${licenses.value.length} exam${licenses.value.length > 1 ? 's' : ''}` ]
        if (licenseMetrics.value.pending) {
            statusArr.push(`${licenseMetrics.value.pending} pending`)
        }
        if (licenseMetrics.value.archived) {
            statusArr.push(`${licenseMetrics.value.archived} archived`)
        }

        return statusArr.join(', ')
    }
})

const firstLicenseBundleColor = computed(() => {
    const examGuid = licenses.value[0]?.examGuid
    const examMetadata = examGuid && latestExamMetadataForGuid(examGuid)
    const bundle = examMetadata && bundleModule.getters.getExamBundle(examMetadata.objectId)

    const blueBundles = [ 'k9T5L3kC0U', 'tVMt7KCrkQ', 'PusoOu7qGQ', 'VEcpw3DhnA' ]
    const greenBundles = [ 'GVCNrq4ixh', '1kS20KPPkH' ]
    const purpleBundles = [ '9970muCFGV', 'uKLwvbsbPV' ]

    if (!bundle) {
        return undefined
    }

    if (blueBundles.includes(bundle.objectId)) {
        return new URL('@/assets/images/exam-background-card-blue.svg', import.meta.url).href
    } else if (greenBundles.includes(bundle.objectId)) {
        return new URL('@/assets/images/exam-background-card-green.svg', import.meta.url).href
    } else if (purpleBundles.includes(bundle.objectId)) {
        return new URL('@/assets/images/exam-background-card-purple.svg', import.meta.url).href
    } else {
        return new URL('@/assets/images/exam-background-card-red.svg', import.meta.url).href
    }
})

onMounted(async () => {
    await Promise.all([
        licenseModule.actions.fetchCurrentOrgLicenses(),
        examModule.actions.fetchCurrentOrgExams(),
        examModule.actions.fetchExamMetadata(),
        organizationModule.actions.fetchCurrentOrganization(),
        studentModule.actions.fetchCurrentOrgStudents(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        bundleModule.actions.fetchBundles(),
    ])

    if (orgStudent.value?.displayFirstName || orgStudent.value?.displayLastName) {
        displayFirstName.value = orgStudent.value.displayFirstName || ''
        displayLastName.value = orgStudent.value.displayLastName || ''
        enableDisplayName.value = true
    }

    email.value = studentEmail.value
})

const latestExamMetadataForGuid = (examGuid: string) => {
    return examModule.getters.getSortedExamMetadataForGuid(examGuid)[0]
}

const updateInstructors = (instructorIdsPerExamGuid: { [key: string]: string[] }) => {
    updatedInstructorOptionsPerExamGuid.value = instructorIdsPerExamGuid
}

const submitUpdateStudent = async () => {
    const orgStudentId = licenses.value?.[0]?.orgStudent?.objectId

    if (!orgStudentId) {
        throw new Error('submitUpdateStudentL Unable to find orgStudent ID')
    }

    if (!currentOrganization.value) {
        throw new Error('submitUpdateStudent: Unable to find current organization')
    }

    isLoading.value = true
    await studentModule.actions.updateStudent({
        email: email.value,
        orgStudentId,
        organizationId: currentOrganization.value.objectId,
        examInstructors: updatedInstructorOptionsPerExamGuid.value,
        displayFirstName: enableDisplayName.value ? displayFirstName.value : undefined,
        displayLastName: enableDisplayName.value ? displayLastName.value : undefined,
    })
    organizationModule.actions.validateOrgState()
    toastModule.actions.displayToast({
        title: 'Changes saved.',
    })
    isLoading.value = false
}

const submitUpdatePendingStudent = async () => {
    const orgStudentId = licenses.value?.[0]?.orgStudent?.objectId

    if (!orgStudentId) {
        throw new Error('submitUpdatePendingStudent: Unable to find orgStudent ID')
    }

    if (!currentOrganization.value) {
        throw new Error('submitUpdatePendingStudent: Unable to find current organization')
    }

    isLoading.value = true
    await studentModule.actions.updateStudent({
        email: email.value,
        customMessage: customMessage.value,
        orgStudentId,
        organizationId: currentOrganization.value.objectId,
        examInstructors: updatedInstructorOptionsPerExamGuid.value,
        resendInviteEmail: true,
    })
    isLoading.value = false
    toastModule.actions.displayToast({
        title: 'Invite sent.',
    })
}

const updateIsLoading = (newIsLoading: boolean) => {
    isLoading.value = newIsLoading
}
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            :name="orgStudentUser ? `Edit ${studentName}` : 'Edit Pending Student'"
            class="edit-pending-student eps"
            :custom-content-styles="{ padding: '0', height: 'calc(100% - 146px)' }"
            :isTeachSidePanel="true"
            @close="emit('close')"
        >
            <div class="eps__top">
                <div class="eps__title">
                    {{ studentLicenseSummary }}
                </div>
                <div v-if="licenses[0] && licenses[0].status === 'consumed'" class="eps__halftone">
                    <img
                        v-if="licenses[0]"
                        class="eps__halftone-image"
                        :src="firstLicenseBundleColor"
                    >
                </div>
                <LicensesList
                    :licenses="licenses"
                    :student-name="studentName"
                    :org-student-id="orgStudentId"
                    :org-student="orgStudent"
                    :org-student-user="orgStudentUser"
                    @updateInstructors="updateInstructors"
                    @loading="updateIsLoading"
                    @close="emit('close')"
                />
            </div>
            <div class="eps__bottom">
                <EditStudentGroups
                    v-if="licenseMetrics.active"
                    :org-student-id="orgStudentId"
                    :student-name="studentName"
                />
                <template v-if="orgStudentUser && (licenseMetrics.active || licenseMetrics.archived)">
                    <div class="eps__display-name">
                        <div class="eps__display-name-toggle">
                            <ToggleSwitch :key="String(enableDisplayName)" v-model="enableDisplayName" />
                            <span>Display Name</span>
                        </div>
                        <Dismissable dismissable-id="editStudentDisplayName" title="What is a display name?">
                            Do you know your student by a different name? Display name is how your student's name
                            will appear to you and your organization.
                        </Dismissable>
                        <div v-if="enableDisplayName" class="eps__display-name-fields">
                            <PocketInput
                                v-model="displayFirstName"
                                label="Display First Name"
                            />
                            <PocketInput
                                v-model="displayLastName"
                                label="Display Last Name"
                            />
                        </div>
                    </div>
                    <div v-if="orgStudentUser" class="eps__name">
                        <PocketInput
                            :modelValue="orgStudentUser.firstName"
                            label="Student's First Name"
                            :disabled="true"
                        />
                        <PocketInput
                            :modelValue="orgStudentUser.lastName"
                            label="Student's Last Name"
                            :disabled="true"
                        />
                    </div>
                </template>
                <div class="eps__email">
                    <PocketInput
                        v-model="email"
                        label="Student's Email"
                        field-type="email"
                        :disabled="!!orgStudentUser"
                    />
                </div>
                <div v-if="!isLoading && !licenseMetrics.active && !licenseMetrics.archived" class="eps__personalize">
                    <label class="eps__label">
                        Personalize
                    </label>
                    <p class="eps__note">
                        Add a note to the invite email (optional)
                    </p>
                    <PocketTextarea
                        v-model="customMessage"
                        height="114px"
                        class="eps__text-field"
                    />
                    <div class="eps__whats-next">
                        <div class="eps__whats-next-title">
                            What's next?
                        </div>
                        <div class="eps__whats-next-text">
                            Students will receive license codes in an email sent by Pocket Prep.
                            Once they activate their license, it will be reflected here and you'll
                            see them listed as 'Active' on the Settings page.
                        </div>
                    </div>
                </div>
            </div>
            <template #action>
                <PocketButton
                    v-if="!licenseMetrics.active && !licenseMetrics.archived"
                    type="primary"
                    :disabled="isLoading || !email"
                    @click="submitUpdatePendingStudent"
                >
                    Resend Invite Email
                </PocketButton>
                <PocketButton
                    v-else
                    type="primary"
                    :disabled="isLoading"
                    @click="submitUpdateStudent"
                >
                    Save Changes
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.eps {
    .dismissable {
        margin-bottom: 30px;
    }

    &__top {
        position: relative;
        background: $barely-background;
        padding: 36px 32px 22px;
    }

    &__bottom {
        padding: 45px 32px 36px;

        .eps__personalize {
            p,
            label {
                margin-left: 12px !important;
            }

            p {
                margin-bottom: 16px;
            }
        }
    }

    &__title {
        font-size: 14px;
        font-weight: 600;
        line-height: 19px;
        margin-bottom: 23px;
        color: $slate-03;
        margin-left: 12px;
        position: relative;
        z-index: 1;
    }

    &__halftone {
        position: absolute;
        right: 30px;
        top: 0;
    }

    &__display-name {
        margin-bottom: 16px;

        .eps__display-name-toggle {
            display: flex;
            align-items: center;
            margin-bottom: 24px;

            span {
                display: inline-block;
                font-size: 14px;
                margin-left: 8px;
                font-weight: 600;
                color: $slate-03;
            }
        }

        .eps__display-name-fields {
            display: flex;
            justify-content: space-between;
            margin-bottom: 16px;

            .input {
                flex: 1;

                &:first-child {
                    margin-right: 6px;
                }

                &:last-child {
                    margin-left: 6px;
                }
            }
        }
    }

    &__name {
        display: flex;
        justify-content: space-between;
        margin-bottom: 16px;

        .input {
            flex: 1;

            &:first-child {
                margin-right: 6px;
            }

            &:last-child {
                margin-left: 6px;
            }
        }
    }

    &__email {
        margin-bottom: 45px;
    }

    &__label {
        font-weight: 600;
    }

    &__note {
        font-size: 14px;
        margin-top: 7px;
    }

    &__whats-next {
        position: relative;
        margin-top: 40px;
        padding: 14px 12px 12px 15px;
        background-color: rgba($sky-blue, 0.3);
        border-radius: 3px;
        border: 1px solid rgba($brand-blue, 0.4);
    }

    &__whats-next-title {
        font-weight: 600;
        font-size: 15px;
        margin-bottom: 9px;
        line-height: 20px;
    }

    &__whats-next-text {
        font-size: 15px;
        line-height: 20px;
        max-width: 444px;
    }
}
</style>
